import { getPrices } from "app/api/runnersApi";
import { BetSlipHelper } from "app/helpers/betSlipHelper";
import { SessionHelper } from "app/helpers/sessionHelper";
import { MultiplesBetSlipType } from "app/types/multiplesBetSlipType";
import { BetSlipStates } from "app/utils/const";
import { cloneDeep, debounce, get } from "lodash";
import * as api from "../../api/multiplesBetsApi";
import type { BetSlipType } from "../../types/betSlipType";
//
const MultiplesBetSlipListModel = {
  state: {
    multiplesBetSlipList: [] as Array<BetSlipType>,
    submittedType: null,
    order: false,
    instantResponse: false,
    multiplierName: "",
    maxStake: 0,
    maxReturn: 0,
    message: "",
    oddsTotal: 0,
    decimalOdds: 0,
    isOrangeFlashActive: false,
    canSubmit: true,
    loading: false,
    status: "",
    isOverMaxReturn: false,
    potentialProfitMessage: "",
    stakeMessage: "",
    isOverMaxStake: false,
    oddsType: null,
    eventIds: [],
    multiplesBetSlipListBetsAddedCount: 0,
    legAddedOrRemoved: false,
    shouldOverrideBetErrorMessage: false,
  },
  reducers: {
    updateMultipleBetSlipPrices: (
      state: Record<string, any>,
      payload: any
    ): any => {
      let multiplesBetSlipListPriceUpdate = cloneDeep(
        state.multiplesBetSlipList
      );
      multiplesBetSlipListPriceUpdate.forEach(multipleBetSlip => {
        payload.length > 0 &&
          payload.forEach((update: any) => {
            if (multipleBetSlip.runnerId === update.runnerId) {
              multipleBetSlip.runner["aggregatedPrices"] =
                update["aggregatedPrices"];
            }
          });
      });

      return {
        ...state,
        multiplesBetSlipList: multiplesBetSlipListPriceUpdate,
      };
    },
    // payload is an array of price updates
    // find if any of the updates are for a leg of the multiple
    // and update the leg odds
    updateLegOddsFromWS(state: Record<string, any>, payloadArray: any[]): any {
      const multiplesBetSlipList = cloneDeep(state.multiplesBetSlipList);
      for (const payload of payloadArray) {
        const leg = multiplesBetSlipList.find(
          leg => leg.runnerId === payload.id
        );
        if (leg) {
          leg.runner.aggregatedPrices = payload.aggregatedPrices;
          const oddsFromPayload =
            payload.aggregatedPrices &&
            BetSlipHelper.getBetSlipOddFromPrice(
              payload.aggregatedPrices[0],
              "AGGREGATED"
            );

          if (oddsFromPayload) {
            leg.odds = oddsFromPayload;
            leg.oddsType = payload.aggregatedPrices[0].oddsType;
          }
        }
      }

      return {
        ...state,
        multiplesBetSlipList: multiplesBetSlipList,
      };
    },
    updateLegOddsFromPricesFetch(
      state: Record<string, any>,
      payload: any
    ): any {
      const multiplesBetSlipList = cloneDeep(state.multiplesBetSlipList);
      multiplesBetSlipList.forEach(leg => {
        payload.length > 0 &&
          payload.forEach((update: any) => {
            if (
              leg.runnerId === update.runnerId &&
              update["aggregatedPrices"] &&
              update["aggregatedPrices"].length > 0
            ) {
              leg.runner["aggregatedPrices"] = update["aggregatedPrices"];
              // bug fix for BET-14905 - issue linked to price view mode
              // where odds not return for non-aggregated prices use sourceOdds instead
              if (
                update["aggregatedPrices"][0].odds === undefined &&
                update["aggregatedPrices"][0].sourceOdds !== undefined
              ) {
                leg.odds = update["aggregatedPrices"][0].sourceOdds;
                leg.oddsType = update["aggregatedPrices"][0].oddsType;
              } else {
                leg.odds = update["aggregatedPrices"][0].odds;
                leg.oddsType = update["aggregatedPrices"][0].oddsType;
              }
            }
          });
      });
      return {
        ...state,
        multiplesBetSlipList: multiplesBetSlipList,
      };
    },

    setValidationRequest(state: Record<string, any>): any {
      return {
        ...state,
        loading: true,
      };
    },
    setValidationError(state: Record<string, any>): any {
      return {
        ...state,
        loading: false,
      };
    },
    /**
     * Toggles the flag for overwriting the block bet error message in the state.
     * @param state - The current state object.
     * @param payload - The boolean value indicating whether to overwrite the block bet error message.
     * @returns The updated state object with the shouldOverrideBetErrorMessage flag.
     */
    toggleShouldOverrideBetErrorMessage(
      state: Record<string, any>,
      payload: boolean
    ) {
      return {
        ...state,
        shouldOverrideBetErrorMessage: payload,
      };
    },
    setLegAddedOrRemovedFalse(
      state: Record<string, any>,
      payload: MultiplesBetSlipType
    ): any {
      return {
        ...state,
        legAddedOrRemoved: false,
      };
    },

    updateMultiplesBetSlipModel(
      state: Record<string, any>,
      payload: MultiplesBetSlipType
    ): any {
      const message = state.shouldOverrideBetErrorMessage
        ? state.message
        : payload.message;
      let canSubmit = !message || message.length === 0;
      const multiplesBetSlipList = cloneDeep(state.multiplesBetSlipList);
      if (payload["multiplierBetLegs"]) {
        multiplesBetSlipList.forEach(multipleBetSlip => {
          payload["multiplierBetLegs"].forEach(leg => {
            // Update odds
            if (leg["runnerId"] === multipleBetSlip["runnerId"]) {
              multipleBetSlip.odds = leg.odds;
              // Add message if message returned, else reset message value
              if (leg.message) {
                multipleBetSlip.message = leg.message;
                canSubmit = false;
              } else {
                multipleBetSlip.message = "";
              }
            }
          });
        });
      }

      return {
        ...state,
        submittedType: payload["submittedType"],
        order: payload.order,
        instantResponse: payload["instantResponse"],
        multiplierName: payload["multiplierName"],
        maxStake: payload["maxStake"],
        maxReturn: payload["maxReturn"],
        canSubmit: canSubmit,
        oddsTotal: payload.odds || 0,
        decimalOdds: payload.decimalOdds || 0,
        multiplesBetSlipList: multiplesBetSlipList,
        loading: false,
        message: message,
        oddsType: payload.oddsType,
      };
    },
    setIsOverMaxReturn(state, payload: boolean) {
      return {
        ...state,
        isOverMaxReturn: payload,
      };
    },
    setIsOverMaxStake(state, payload: boolean) {
      return {
        ...state,
        isOverMaxStake: payload,
      };
    },
    addMultiplesBetSlip(
      state: Record<string, any>,
      payload: MultiplesBetSlipType
    ): any {
      if (state.status && state.status === BetSlipStates.FINISHED) {
        return state;
      }
      const multiplesBetSlipList = state.multiplesBetSlipList;

      const eventIds = Array.isArray(state.eventIds)
        ? cloneDeep(state.eventIds)
        : [];

      multiplesBetSlipList.push(payload);
      // add selected multiple bet event id to eventIds array
      // the eventIds are used for counting the number of multiple bets in the multiples bet slip
      eventIds.push(payload.eventId);

      return {
        ...state,
        multiplesBetSlipList: multiplesBetSlipList,
        isOrangeFlashActive: true,
        eventIds: eventIds,
        multiplesBetSlipListBetsAddedCount:
          state.multiplesBetSlipListBetsAddedCount + 1,
        legAddedOrRemoved: true,
      };
    },
    removeMultipleLeg(state: Record<string, any>, payload: number): any {
      const multiplesBetSlipList = cloneDeep(state.multiplesBetSlipList);
      let eventIds = cloneDeep(state.eventIds);
      let eventIdToRemove = -1;

      const index = multiplesBetSlipList.findIndex(item => {
        if (item["runnerId"] === payload) {
          eventIdToRemove = item.eventId;
          return item["runnerId"] === payload;
        }
      });
      // find eventId for payload runnerId
      let indexOfEventIdToRemove = eventIds.findIndex(
        eventId => eventId === eventIdToRemove
      );
      // remove selected event from eventIds array
      eventIdToRemove !== -1 && eventIds.splice(indexOfEventIdToRemove, 1);
      // remove selected let from multiplesBetSlipList
      index !== -1 && multiplesBetSlipList.splice(index, 1);

      return {
        ...state,
        multiplesBetSlipList,
        eventIds: eventIds,
        multiplesBetSlipListBetsAddedCount:
          state.multiplesBetSlipListBetsAddedCount - 1,
        legAddedOrRemoved: true,
      };
    },
    resetOddsTotal(): any {
      return {
        oddsTotal: 0,
      };
    },
    clearMultiples(): any {
      return {
        multiplesBetSlipList: [],
        submittedType: null,
        order: false,
        instantResponse: false,
        multiplierName: "",
        maxStake: 0,
        maxReturn: 0,
        odds: 0,
        oddsType: null,
        eventIds: [],
        multiplesBetSlipListBetsAddedCount: 0,
        shouldOverrideBetErrorMessage: false,
      };
    },
    updateMultiplesBetSlipList: (
      state: Record<string, any>,
      payload: Record<string, any>
    ): any => {
      return { ...state, multiplesBetSlipList: payload };
    },
    setFlashFlag: (state: Record<string, any>): any => {
      return { ...state, isOrangeFlashActive: false };
    },
    setMultipleBetResponse: (state, payload) => {
      return { ...state, ...payload };
    },
    setMultipleBetMessage: (state, payload) => {
      return { ...state, message: payload };
    },
    setMultiplesBetSlipState: (state, payload) => {
      return { ...state, multiplesBetSlipState: payload };
    },
    updateMultiplesBetSlip: (state, payload) => {
      if (!payload) {
        return { ...state };
      }
      return { ...state, ...payload };
    },
  },
  effects: {
    validateMultiples: debounce(async function (
      payload: Record<string, any>,
      rootState: Record<string, any>
    ) {
      // If the multiples bet slip model is currently loading, exit the function
      if (rootState.multiplesBetSlipListModel.loading) {
        return;
      }

      if (
        rootState.multiplesBetSlipListModel.status === BetSlipStates.FINISHED
      ) {
        return;
      }

      // If the multiples bet slip list is empty, exit the function
      if (
        rootState.multiplesBetSlipListModel.multiplesBetSlipList &&
        rootState.multiplesBetSlipListModel.multiplesBetSlipList.length === 0
      ) {
        return;
      }

      // Set a validation request flag
      this.setValidationRequest();

      // Prepare the validation object with necessary data
      const validationObj = {
        multiplierBetLegs: [] as Array<any>,
        oddsType: localStorage.getItem("oddsType"),
        platform: SessionHelper.isAsianRouteAndSessionView()
          ? "asian"
          : "european",
      };

      // Iterate over each multiple in the bet slip list and populate the bet legs array
      for (const multiple of rootState.multiplesBetSlipListModel
        .multiplesBetSlipList) {
        validationObj["multiplierBetLegs"].push({
          odds: multiple.odds,
          runnerId: multiple.runnerId,
          oddsType: multiple.oddsType,
        });
      }

      try {
        //validate multiples via API validateMultiples call using the validation object
        const resp = await api.validateMultiples(validationObj);
        // If the response data is available, update the multiples bet slip model
        if (resp.data) {
          this.updateMultiplesBetSlipModel(resp.data);
        }
      } catch (error) {
        // Log any errors that occur during the API call
        console.error("Error occurred during API call:", error);
        // Set a validation error flag
        this.setValidationError();
      }
    },
    300),

    async fetchPricesForLegs(
      payload: Record<string, any>,
      rootState: Record<string, any>
    ) {
      if (
        !rootState.multiplesBetSlipListModel.multiplesBetSlipList &&
        rootState.multiplesBetSlipListModel.multiplesBetSlipList &&
        rootState.multiplesBetSlipListModel.multiplesBetSlipList.length === 0
      ) {
        return;
      }

      const betSlipListClone = cloneDeep(
        rootState.multiplesBetSlipListModel.multiplesBetSlipList
      );

      this.resetOddsTotal();

      betSlipListClone.forEach((leg: MultiplesBetSlipType) => {
        getPrices(leg.runnerId)
          .then(resp => {
            const odds = get(resp, ["aggregatedPrices", 0, "odds"]);
            if (odds) {
              leg.odds = odds;
            }
          })
          .catch(error => {
            console.log("error: ", error);
          });
      });
      if (betSlipListClone && betSlipListClone.length > 0) {
        this.updateMultiplesBetSlipList(betSlipListClone);
      }
    },
    //
  },
};
export default MultiplesBetSlipListModel;
